import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { Select, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DropResult } from "@hello-pangea/dnd";
import { PageHeader } from "../../../components/PageHeader";
import { TableSkeleton } from "../../../components/SkeletonLoader";
import {
  CustomFieldRow,
  CustomFieldsTable,
} from "../../../components/CustomFieldsTable";
import { useAllowedObjectTypes } from "../shared/objectTypeOptions";
import {
  CUSTOM_FIELD_DEFINITION_FRAGMENT,
  CustomFieldDefinition,
} from "../shared/customFieldFragments";

const DEFINITIONS_QUERY = gql`
  query customFieldDefinitions($objectType: ObjectType!) {
    customFieldDefinitions(objectType: $objectType) {
      ...CustomFieldDefinitionFields
    }
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`;

const DELETE_DEFINITION = gql`
  mutation deleteCustomFieldDefinition($id: ID!) {
    deleteCustomFieldDefinition(id: $id)
  }
`;

const REORDER_DEFINITIONS = gql`
  mutation reorderCustomFieldDefinitions(
    $objectType: ObjectType!
    $orderedIds: [ID!]!
  ) {
    reorderCustomFieldDefinitions(
      objectType: $objectType
      orderedIds: $orderedIds
    ) {
      ...CustomFieldDefinitionFields
    }
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`;

const toTableRows = (definitions: CustomFieldDefinition[]): CustomFieldRow[] =>
  definitions.map((def) => ({
    id: def.id,
    name: def.name,
    description: def.description,
    propertyKey: def.key,
    type: def.type,
    required: def.required,
    unique: def.unique,
    visibility: def.visibility,
    editability: def.editability,
  }));

export const CustomFieldDefinitions = () => {
  const navigate = useNavigate();
  const { options: objectTypeOptions, loading: loadingTypes } =
    useAllowedObjectTypes();
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery<{
    customFieldDefinitions: CustomFieldDefinition[];
  }>(DEFINITIONS_QUERY, {
    variables: { objectType: selectedType },
    skip: !selectedType,
  });

  const [deleteMutation] = useMutation(DELETE_DEFINITION);
  const [reorderMutation] = useMutation(REORDER_DEFINITIONS);

  const definitions = data?.customFieldDefinitions ?? [];

  const handleEdit = (index: number) => {
    if (!selectedType) return;
    const def = definitions[index];
    navigate(`${def.id}/edit?objectType=${encodeURIComponent(selectedType)}`);
  };

  const handleDelete = async (index: number, name: string) => {
    const def = definitions[index];
    const confirmed = await new Promise<boolean>((resolve) =>
      modals.openConfirmModal({
        title: "Delete Custom Field",
        children: (
          <Text>
            Are you sure you want to delete the field &quot;{name}&quot;? This
            will also delete all values associated with this field.
          </Text>
        ),
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      }),
    );

    if (!confirmed) return;

    try {
      await deleteMutation({ variables: { id: def.id } }).then((result) => {
        if (result?.error) {
          throw new Error(result.error.message);
        }
      });

      notifications.show({
        title: "Field deleted",
        message: `Custom field "${name}" has been deleted`,
        color: "green",
        autoClose: 3000,
      });

      refetch();
    } catch (error) {
      notifications.show({
        title: "Delete failed",
        message:
          error instanceof Error && error.message.length < 200
            ? error.message
            : "Failed to delete custom field",
        color: "red",
        autoClose: 5000,
      });
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !selectedType) return;

    const items = [...definitions];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const orderedIds = items.map((item) => item.id);

    try {
      await reorderMutation({
        variables: { objectType: selectedType, orderedIds },
      }).then((res) => {
        if (res?.error) {
          throw new Error(res.error.message);
        }
      });

      notifications.show({
        title: "Order updated",
        message: "Custom field order has been saved",
        color: "green",
        autoClose: 2000,
      });

      refetch();
    } catch (error) {
      notifications.show({
        title: "Reorder failed",
        message:
          error instanceof Error && error.message.length < 200
            ? error.message
            : "Failed to reorder custom fields",
        color: "red",
        autoClose: 5000,
      });
      refetch();
    }
  };

  const tableRows = toTableRows(definitions);

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Custom Field Definitions"
        subtitle="Define custom fields for your entities"
        showBreadcrumbs={false}
        actions={[
          {
            label: "Create Definition",
            icon: <IconPlus size={16} />,
            onClick: () => navigate("create"),
          },
        ]}
      />

      <Stack gap="md">
        <Select
          label="Object Type"
          placeholder={loadingTypes ? "Loading..." : "Select an object type"}
          data={objectTypeOptions}
          value={selectedType}
          onChange={setSelectedType}
          searchable
          clearable
          disabled={loadingTypes}
          style={{ maxWidth: 400 }}
        />

        {!selectedType && (
          <Text c="dimmed">
            Select an object type to view its custom field definitions.
          </Text>
        )}

        {selectedType && loading && <TableSkeleton rows={5} columns={6} />}

        {selectedType && !loading && definitions.length === 0 && (
          <Text c="dimmed">
            No custom field definitions found for this object type.
          </Text>
        )}

        {selectedType && !loading && definitions.length > 0 && (
          <CustomFieldsTable
            fields={tableRows}
            onDragEnd={handleDragEnd}
            onEdit={handleEdit}
            onDelete={handleDelete}
            droppableId="definitions"
          />
        )}
      </Stack>
    </div>
  );
};
